import React from 'react';
import c from 'classnames';
import config from 'react-global-configuration';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Button, Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { ParagraphText as Type } from '../../../types/uncinc_profile/paragraphs';
import {
  PARAGRAPH_VIEW_MODE_DEFAULT,
  PARAGRAPH_VIEW_MODE_FULLSCREEN,
  PARAGRAPH_VIEW_MODE_HOMEPAGE,
  PARAGRAPH_VIEW_MODE_CAMPAIGN_COLOR_THEME,
  ZENCHEF_CLASSES,
  DOMAIN_AMSTERDAMUNDERGROUND,
} from '../../../config/constants';

import './style.scss';

interface ParagraphTextProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<Type>;
  viewMode?: string;
}

// eslint-disable-next-line max-len
const ParagraphText: React.FC<ParagraphTextProps> = ({ entity, viewMode = PARAGRAPH_VIEW_MODE_DEFAULT }) => {
  const viewModeIsFullscreen = viewMode === PARAGRAPH_VIEW_MODE_FULLSCREEN;
  const viewModeIsHomepage = viewMode === PARAGRAPH_VIEW_MODE_HOMEPAGE;
  const viewModeIsCampaignPageColorTheme = viewMode === PARAGRAPH_VIEW_MODE_CAMPAIGN_COLOR_THEME;
  const currentDomainIsAmsterdamUnderground = config.get('CURRENT_DOMAIN') === DOMAIN_AMSTERDAMUNDERGROUND;

  const paragraph = entity.toJS();
  const title = paragraph.field_title[0]?.value;
  const text = paragraph.field_text[0].processed;
  const linkUrl = paragraph.field_link[0]?.alias || paragraph.field_link[0]?.uri;
  const linkTitle = paragraph.field_link[0]?.title;
  const linkClasses = paragraph.field_link[0]?.options?.attributes?.class;
  const secondaryLinkUrl = paragraph.field_secondary_link[0]?.alias ||
    paragraph.field_secondary_link[0]?.uri;
  const secondaryLinkTitle = paragraph.field_secondary_link[0]?.title;
  const secondaryLinkClasses = paragraph.field_secondary_link[0]?.options?.attributes?.class;
  const moreThanOneParagraph = (text.match(/<p/g) || []).length;

  const paragraphClassNames = c('ParagraphText', {
    ParagraphText__FullScreen: viewModeIsFullscreen,
    ParagraphText__HomePage: viewModeIsHomepage,
  });

  const colClasses = c('col-12 col-md-10 offset-md-1', {
    'col-lg-8 offset-lg-2': !viewModeIsCampaignPageColorTheme,
    'col-lg-7 offset-lg-0': viewModeIsCampaignPageColorTheme,
  });

  return (
    <div className={paragraphClassNames}>
      <Container>
        <div className="row">
          <div className={colClasses}>
            {title && (
              <div className="ParagraphText__title">
                <h2>{title}</h2>
              </div>
            )}

            <div className="ParagraphText__text">
              <WysiwygField
                className={((viewModeIsFullscreen || viewModeIsHomepage) && moreThanOneParagraph) ? 'u-multiblock' : ''}
                property="schema:text"
                html={text}
              />
            </div>

            {(linkUrl || secondaryLinkUrl) && (
              <div className="ParagraphText__link">
                {linkUrl && (
                  <Button
                    className={c(linkClasses)}
                    href={linkUrl.match(/<nolink>/) === null ? linkUrl : ''}
                    {...(linkClasses?.[0] && linkClasses[0].includes(ZENCHEF_CLASSES.OPEN) && {
                      'data-zc-action': 'open',
                    })}
                  >
                    {linkTitle}
                    {!currentDomainIsAmsterdamUnderground && <i className="icon icon-arrow-right" />}
                  </Button>
                )}

                {secondaryLinkUrl && (
                  <Button
                    className={c('Button--secondary', secondaryLinkClasses)}
                    href={secondaryLinkUrl}
                  >
                    {secondaryLinkTitle}
                    {!currentDomainIsAmsterdamUnderground && <i className="icon icon-arrow-right" />}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphText;
